import moment from 'moment-timezone';

import type { Location } from '../constants/timezone';

import { TIMEZONE_DISPLAY_NAMES, ALL_TIMEZONES } from '../constants/timezone';

/**
 * Build a list of 15-minute timeslots between start and end (end exclusive).
 * Example: 09:00 → 10:00 => ["09:00","09:15","09:30","09:45"]
 */
export function getQuarterHours(start: string, end: string) {
  const partsStart = start.split(':');
  const partsEnd = end.split(':');
  const sh = Number(partsStart[0]);
  const sm = Number(partsStart[1]);
  const eh = Number(partsEnd[0]);
  const em = Number(partsEnd[1]);

  const startMin = sh * 60 + sm;
  const endMin = eh * 60 + em;
  const out = [];

  for (let m = startMin; m < endMin; m += 15) {
    const hh = String(Math.floor(m / 60)).padStart(2, '0');
    const mm = String(m % 60).padStart(2, '0');
    out.push(hh + ':' + mm);
  }

  return out;
}

// 12-hour formatter used for user-facing times
export function formatTime12(t: string) {
  const parts = t.split(':');
  let h = Number(parts[0]);
  const m = Number(parts[1]);
  // 24:00 is end-of-day midnight; display as 12 AM
  if (h === 24) h = 0;
  const hr = h % 12 === 0 ? 12 : h % 12;
  const am = h < 12 ? 'AM' : 'PM';
  const mm = String(m).padStart(2, '0');
  return hr + ':' + mm + ' ' + am;
}

/**
 * Generic time formatter honoring hour mode.
 * - mode === '24h' -> returns HH:MM
 * - otherwise -> 12h with AM/PM
 */
export function formatTime(t: string, mode: string) {
  if (mode === '24h') {
    const [h, m] = t.split(':').map(Number);
    return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
  }
  return formatTime12(t);
}

/**
 * Convert hour number to HH:MM string format.
 * Example: 8 → "08:00", 14 → "14:00"
 */
export function hourToTimeString(hour: number): string {
  return String(hour).padStart(2, '0') + ':00';
}

/**
 * Normalize time string to HH:MM format (removes seconds if present).
 * Example: "08:00:00" → "08:00", "14:00" → "14:00"
 */
export function normalizeTimeString(timeString: string): string {
  const parts = timeString.split(':');
  return `${parts[0]}:${parts[1]}`;
}

/**
 * Convert HH:MM string format to hour number.
 * Example: "08:00" → 8, "14:00" → 14
 */
export function timeStringToHour(timeString: string): number {
  return parseInt(timeString.split(':')[0]);
}

// Compact time formatter for use in tight spaces (e.g., calendar grid gutter).
// Removes AM/PM to save space.
export function formatCompactTimeFormat(time: string): string {
  return time.replace('AM', '').replace('PM', '');
}
// -----------------------------------------------------------------------------
// Timezone-aware helpers for absolute datetime storage
// -----------------------------------------------------------------------------

/** Convert HH:MM string to total minutes */
export function hmToMinutes(hm: string): number {
  const [h, m] = hm.split(':').map(Number);
  return h * 60 + m;
}

/** Convert total minutes to HH:MM (24h) */
export function minutesToHM(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
}

/** Given a gridStart (HH:MM) and an index (0-based, 15-min slots), return HH:MM */
export function indexToHM(gridStart: string, index: number): string {
  const base = hmToMinutes(gridStart);
  const mins = base + index * 15;
  return minutesToHM(mins);
}

/**
 * Build a UTC ISO string from a date (year, month 1-12, day) and a time (HH:MM)
 * interpreted in the provided IANA timezone.
 */
export function toUtcIsoFromParts(
  year: number,
  month: number, // 1-12
  day: number,
  hm: string,
  tz: string,
): string {
  const [hh, mm] = hm.split(':').map(Number);
  const m = moment.tz(
    { year, month: month - 1, day, hour: hh, minute: mm },
    tz,
  );
  return m.toDate().toISOString();
}

/**
 * From a UTC ISO string, compute the HH:MM of that instant in the target timezone.
 */
export function isoUtcToHM(isoUtc: string, tz: string): string {
  const m = moment.utc(isoUtc).tz(tz);
  const h = m.hour();
  const min = m.minute();
  return minutesToHM(h * 60 + min);
}

// Compute the UTC interval for a given cell (column ymd + row index)
// Basis: interpret the slot HH:MM in the owner's configured timezone if present;
// otherwise fall back to the currently selected viewer timezone. This aligns
// the cell semantics and the gutter labels when viewers switch timezones.
export const getCellUtcInterval = (
  ymd: string,
  row: number,
  gridStart: string,
  gridEnd: string,
  timezone: string,
): { startIso: string; endIso: string } => {
  const { y, m, d } = parseYmd(ymd);
  const baseTz = timezone;
  const startHM = indexToHM(gridStart, row);
  const endHM = indexToHM(
    gridStart,
    Math.min(row + 1, getQuarterHours(gridStart, gridEnd).length),
  );
  const startIso = toUtcIsoFromParts(y, m, d, startHM, baseTz);
  const endIso = toUtcIsoFromParts(y, m, d, endHM, baseTz);
  return { startIso, endIso };
};

/** Parse a YYYY-MM-DD string into components */
export function parseYmd(ymd: string): { y: number; m: number; d: number } {
  const [y, m, d] = ymd.split('-').map(Number);
  return { y, m, d };
}

/** Add n days to a YYYY-MM-DD and return a new YYYY-MM-DD */
export function addDaysYmd(ymd: string, n: number): string {
  const { y, m, d } = parseYmd(ymd);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + n);
  const yyyy = String(dt.getFullYear()).padStart(4, '0');
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Difference in days between two YYYY-MM-DD (b - a) */
export function diffDaysYmd(a: string, b: string): number {
  const pa = parseYmd(a);
  const pb = parseYmd(b);
  const da = new Date(pa.y, pa.m - 1, pa.d);
  const db = new Date(pb.y, pb.m - 1, pb.d);
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.floor((db.getTime() - da.getTime()) / msPerDay);
}

/** Create a stable dateKey (Date.toDateString) from YYYY-MM-DD */
export function dateKeyFromYmd(ymd: string): string {
  const { y, m, d } = parseYmd(ymd);
  return new Date(y, m - 1, d).toDateString();
}

/**
 * Convert a Date to YYYY-MM-DD in a specific timezone, interpreting the calendar day
 * in that timezone (not converting the instant which could shift the day).
 */
export function toYmd(d: Date, tz: string): string {
  const y = d.getFullYear();
  const mo = d.getMonth(); // 0-based
  const day = d.getDate();
  const m = moment.tz({ year: y, month: mo, day: day, hour: 0, minute: 0 }, tz);
  const yyyy = String(m.year()).padStart(4, '0');
  const mm = String(m.month() + 1).padStart(2, '0');
  const dd = String(m.date()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Format a column date given a YYYY-MM-DD using an owner/base timezone and a viewer timezone.
 * Ensures DST-correct day anchoring by using noon in the base timezone.
 */
export function formatColumnDate(
  ymd: string,
  fmt: string,
  baseTimezone: string,
  viewerTimezone: string,
): string {
  const { y, m, d } = parseYmd(ymd);
  const mOwner = moment.tz(
    { year: y, month: m - 1, day: d, hour: 12, minute: 0 },
    baseTimezone,
  );
  return mOwner.clone().tz(viewerTimezone).format(fmt);
}

/**
 * Convert an HH:MM defined in baseTimezone at the provided YYYY-MM-DD into the same
 * instant's HH:MM in viewerTimezone (24h string).
 */
export function hmToViewerAtYmd(
  hm: string,
  refYmd: string,
  baseTimezone: string,
  viewerTimezone: string,
): string {
  const [hh, mm] = hm.split(':').map(Number);
  const { y, m, d } = parseYmd(refYmd);
  const mBase = moment.tz(
    { year: y, month: m - 1, day: d, hour: hh, minute: mm },
    baseTimezone,
  );
  return mBase.clone().tz(viewerTimezone).format('HH:mm');
}

/**
 * Converts a time from base timezone to viewer timezone and returns the moment object.
 * This is useful when you need both the date and time in the viewer's timezone,
 * especially when the time conversion might cross midnight.
 */
export function hmToViewerMoment(
  hm: string,
  refYmd: string,
  baseTimezone: string,
  viewerTimezone: string,
): moment.Moment {
  const [hh, mm] = hm.split(':').map(Number);
  const { y, m, d } = parseYmd(refYmd);
  const mBase = moment.tz(
    { year: y, month: m - 1, day: d, hour: hh, minute: mm },
    baseTimezone,
  );
  return mBase.clone().tz(viewerTimezone);
}

/**
 * Converts a timezone value (e.g., "America/Los_Angeles") to a formatted name with GMT offset
 * @param timezone - IANA timezone identifier
 * @param offsetPosition - Position of GMT offset: 'start' for "(GMT-05:00) Eastern Time" or 'end' for "Pacific Time - Los Angeles (GMT-08:00)" (default: 'end')
 * @returns Formatted timezone name with GMT offset
 */
export function formatTimezoneWithOffset(
  timezone: string,
  offsetPosition: 'start' | 'end' = 'end',
): string {
  let baseName: string;

  // 1) Prefer explicit mapping from constants (already in "Time - City" format)
  if (TIMEZONE_DISPLAY_NAMES[timezone]) {
    baseName = TIMEZONE_DISPLAY_NAMES[timezone];
  } else {
    // 2) Fallback: try to get a readable name from timezone (e.g. Europe/Istanbul)
    const timeName = `${timezone} Time`;

    // Try to get city name from location map first (more accurate)
    const location = getTimezoneToLocationMap().get(timezone);
    let cityName: string;
    if (location && location.city) {
      cityName = location.city;
    } else {
      // Fallback: extract city name from timezone identifier (e.g., "America/Los_Angeles" -> "Los Angeles")
      const parts = timezone.split('/');
      cityName = parts[parts.length - 1].replace(/_/g, ' ');
    }

    baseName = `${timeName} - ${cityName}`;
  }

  // Get GMT offset
  try {
    const m = moment.tz(timezone);
    const offset = m.format('Z'); // "-08:00"
    if (offset) {
      if (offsetPosition === 'start') {
        return `(GMT${offset}) ${baseName}`;
      } else {
        return `${baseName} (GMT${offset})`;
      }
    }
  } catch {
    // If moment fails, just return the base name
  }

  return baseName;
}

/**
 * Get all unique timezone values from ALL_TIMEZONES, sorted by GMT offset
 * @returns Array of timezone values sorted by GMT offset (west to east)
 */
export function getAllUniqueTimezones(): string[] {
  const uniqueTimezones = new Set<string>();

  // Extract unique timezones from ALL_TIMEZONES
  ALL_TIMEZONES.forEach((loc) => {
    uniqueTimezones.add(loc.timezone);
  });

  // Convert to array and sort by GMT offset
  const timezoneArray = Array.from(uniqueTimezones);

  return timezoneArray.sort((a, b) => {
    try {
      const offsetA = moment.tz(a).utcOffset();
      const offsetB = moment.tz(b).utcOffset();
      return offsetA - offsetB; // Sort from west (negative) to east (positive)
    } catch {
      return a.localeCompare(b); // Fallback to alphabetical if moment fails
    }
  });
}

/**
 * Finds a timezone in the available timezone list that matches the GMT offset of the given timezone
 * @param timezone - IANA timezone identifier (e.g., "America/Detroit")
 * @returns Matching timezone from the available list, or null if no match found
 */
export function findTimezoneByOffset(timezone: string): string | null {
  try {
    const targetOffset = moment.tz(timezone).utcOffset();
    const availableTimezones = getAllUniqueTimezones();
    
    // Find the first timezone with matching offset
    for (const tz of availableTimezones) {
      try {
        const tzOffset = moment.tz(tz).utcOffset();
        if (tzOffset === targetOffset) {
          return tz;
        }
      } catch {
        // Skip invalid timezones
        continue;
      }
    }
  } catch {
    // If we can't get the offset, return null
  }
  
  return null;
}

/**
 * Creates a map of timezone strings to Location objects
 * @returns Map of timezone identifiers to Location objects
 */
export function createTimezoneToLocationMap(): Map<string, Location> {
  const map = new Map<string, Location>();

  // Initialize the map with all timezones
  ALL_TIMEZONES.forEach((loc) => {
    map.set(loc.timezone, loc);
  });

  return map;
}

// Cache the map to avoid recreating it on every access
let cachedTimezoneMap: Map<string, Location> | null = null;

/**
 * Gets the timezone to location map, creating it if necessary
 * @returns Map of timezone identifiers to Location objects
 */
function getTimezoneToLocationMap(): Map<string, Location> {
  if (!cachedTimezoneMap) {
    cachedTimezoneMap = createTimezoneToLocationMap();
  }
  return cachedTimezoneMap;
}

/**
 * Formats a date string or Date object into a readable format.
 * Example: "Jan 15, 2024 at 3:30 PM"
 */
export function formatDateTime(
  date: string | Date,
  timezone?: string,
): string {
  const tz = timezone || moment.tz.guess();
  const m =
    typeof date === 'string' ? moment(date).tz(tz) : moment(date).tz(tz);
  return m.format('MMM D, YYYY [at] h:mm A');
}
